import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Check, X, ShieldCheck, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { api, setStoredToken, setStoredUser, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";

// --- Client-side password policy (advisory only; server is the source of truth) ---
const POLICY = [
  { key: "len",   label: "At least 14 characters",       test: (p) => p.length >= 14 },
  { key: "upper", label: "At least one uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { key: "lower", label: "At least one lowercase letter", test: (p) => /[a-z]/.test(p) },
  { key: "num",   label: "At least one number",           test: (p) => /\d/.test(p) },
  { key: "sym",   label: "At least one symbol",           test: (p) => /[^A-Za-z0-9]/.test(p) },
];

function PolicyRow({ ok, label, testId }) {
  return (
    <li
      data-testid={testId}
      className="flex items-center gap-2 text-[13px]"
      aria-live="polite"
    >
      {ok ? (
        <Check className="w-3.5 h-3.5 text-green-700" aria-label="Met" />
      ) : (
        <X className="w-3.5 h-3.5 text-muted-foreground" aria-label="Not yet" />
      )}
      <span className={ok ? "text-foreground" : "text-muted-foreground"}>{label}</span>
    </li>
  );
}

function PasswordField({ id, label, value, onChange, testId, autoComplete }) {
  const [show, setShow] = useState(false);
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <Input
          id={id}
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
          data-testid={testId}
          className="pr-10 font-mono tracking-tight"
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          aria-label={show ? "Hide password" : "Show password"}
          data-testid={`${testId}-toggle`}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground transition-opacity"
          tabIndex={-1}
        >
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

export default function ChangePassword() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // Client-side policy evaluation (advisory; the server enforces)
  const policyState = useMemo(
    () => POLICY.map((rule) => ({ ...rule, ok: rule.test(newPw) })),
    [newPw]
  );
  const allPolicyOk = policyState.every((r) => r.ok);
  const matches = confirmPw.length > 0 && confirmPw === newPw;
  const notSameAsCurrent = newPw.length > 0 && newPw !== currentPw;
  const canSubmit =
    !submitting &&
    currentPw.length > 0 &&
    newPw.length > 0 &&
    confirmPw.length > 0 &&
    allPolicyOk &&
    matches &&
    notSameAsCurrent;

  // Clear any inline error message when the user edits any field
  useEffect(() => {
    if (errorMsg) setErrorMsg("");
  }, [currentPw, newPw, confirmPw, errorMsg]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setErrorMsg("");
    try {
      await api.post("/auth/change-password", {
        current_password: currentPw,
        new_password: newPw,
        confirm_new_password: confirmPw,
      });
      // Defensive: blank out local state immediately so the values don't
      // sit in memory longer than needed.
      setCurrentPw("");
      setNewPw("");
      setConfirmPw("");
      // Clear stored session and force re-login with the new password.
      setStoredToken(null);
      setStoredUser(null);
      toast.success("Password updated. Please sign in again with your new password.", {
        duration: 6000,
      });
      // Short delay so the toast is visible before redirect.
      setTimeout(() => {
        window.location.href = "/login";
      }, 400);
    } catch (err) {
      // Surface a generic message; never echo any password content.
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && Array.isArray(detail.failures)) {
        setErrorMsg(
          (detail.message || "New password does not meet the security policy.") +
            "\n• " +
            detail.failures.join("\n• ")
        );
      } else if (typeof detail === "string") {
        setErrorMsg(detail);
      } else {
        setErrorMsg(errMsg(err, "Could not change password. Please try again."));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-2xl mx-auto" data-testid="change-password-page">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2">
          Account · Security
        </div>
        <h1 className="font-display text-3xl md:text-4xl tracking-tight">Change password</h1>
        <p className="text-sm text-muted-foreground mt-2">
          Update the password for your Kandake account. After a successful change you will be
          signed out everywhere and asked to sign in again with your new password.
        </p>
      </div>

      <Card>
        <CardHeader className="space-y-2">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-primary" />
            <CardTitle className="text-lg">Authenticated password change</CardTitle>
          </div>
          <CardDescription>
            Signed in as{" "}
            <span className="font-medium text-foreground" data-testid="change-password-current-user">
              {user?.name || user?.email}
            </span>
            . The new password applies only to this account.
          </CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="change-password-form" autoComplete="off">
            <PasswordField
              id="cp-current"
              label="Current password"
              value={currentPw}
              onChange={setCurrentPw}
              testId="change-password-current-input"
              autoComplete="current-password"
            />

            <Separator />

            <PasswordField
              id="cp-new"
              label="New password"
              value={newPw}
              onChange={setNewPw}
              testId="change-password-new-input"
              autoComplete="new-password"
            />

            <ul
              className="rounded-md border bg-secondary/40 px-4 py-3 space-y-1.5"
              data-testid="change-password-policy-list"
              aria-label="Password requirements"
            >
              {policyState.map((r) => (
                <PolicyRow
                  key={r.key}
                  ok={r.ok}
                  label={r.label}
                  testId={`change-password-policy-${r.key}`}
                />
              ))}
              <PolicyRow
                ok={notSameAsCurrent}
                label="Different from your current password"
                testId="change-password-policy-not-same"
              />
            </ul>

            <PasswordField
              id="cp-confirm"
              label="Confirm new password"
              value={confirmPw}
              onChange={setConfirmPw}
              testId="change-password-confirm-input"
              autoComplete="new-password"
            />
            {confirmPw.length > 0 && !matches && (
              <p
                className="text-[12.5px] text-destructive flex items-center gap-1.5"
                data-testid="change-password-match-error"
              >
                <X className="w-3.5 h-3.5" />
                New password and confirmation do not match.
              </p>
            )}

            {errorMsg && (
              <Alert variant="destructive" data-testid="change-password-error">
                <AlertCircle className="w-4 h-4" />
                <AlertDescription className="whitespace-pre-wrap">{errorMsg}</AlertDescription>
              </Alert>
            )}

            <div className="flex items-center justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate(-1)}
                disabled={submitting}
                data-testid="change-password-cancel-button"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!canSubmit}
                data-testid="change-password-submit-button"
              >
                {submitting ? "Updating…" : "Update password"}
              </Button>
            </div>

            <p className="text-[11px] text-muted-foreground pt-2 border-t">
              Tip: use a unique, randomly generated password from your password manager. Kandake
              never stores your plaintext password and never sends passwords over email.
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
